import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId } from "../../utils/currentSalon";
import SBINModal from "../../components/modals/SBINModal";
import ModalCloseX from "../../components/common/ModalCloseX";
import {
  createFeedbackTemplate,
  listFeedbackTemplates,
  updateFeedbackTemplate,
} from "../../api/marketingFeedback";
import "../MarketingSectionCompact.css";

const TEMPLATE_TOKENS = [
  {
    token: "{{client_full_name}}",
    label: "Client name",
    source: "Client on the feedback follow-up ticket.",
  },
  {
    token: "{{client_phone}}",
    label: "Client phone",
    source: "Client phone saved on the ticket or client profile.",
  },
  {
    token: "{{ticket_number}}",
    label: "Ticket number",
    source: "The ticket tied to the feedback follow-up.",
  },
  {
    token: "{{service_date_local}}",
    label: "Service date",
    source: "Ticket close date shown in the salon time zone.",
  },
  {
    token: "{{staff_name}}",
    label: "Staff name",
    source: "Technician linked to the feedback request.",
  },
  {
    token: "{{manager_name}}",
    label: "Manager name",
    source: "Follow-up owner; if unassigned, the person sending the email.",
  },
  {
    token: "{{salon_name}}",
    label: "Salon name",
    source: "Current salon name.",
  },
  {
    token: "{{salon_phone}}",
    label: "Salon phone",
    source: "Salon phone, or business phone if salon phone is missing.",
  },
];

const defaultForm = {
  name: "",
  channel: "email",
  subject: "",
  body: "",
};

const renderTokens = (template, values) =>
  String(template || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (match, key) =>
    values?.[key] ? String(values[key]) : ""
  );

const displayName = (user) =>
  user?.full_name || user?.name || user?.username || user?.email || "";

export default function ResponseTemplates() {
  const navigate = useNavigate();
  const { user, token, currentSalon } = useAuth();
  const salonId = getCurrentSalonId(user);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listFeedbackTemplates(salonId, token, {
        include_inactive: true,
      });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load templates.");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [salonId, token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setForm(defaultForm);
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (template) => {
    setForm({
      name: template?.name || "",
      channel: template?.channel || "email",
      subject: template?.subject || "",
      body: template?.body || "",
    });
    setEditingId(template?.id || null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!salonId) return;
    const trimmedName = form.name.trim();
    const trimmedBody = form.body.trim();
    if (!trimmedName || !trimmedBody) {
      setError("Template name and body are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await updateFeedbackTemplate(
          editingId,
          {
            name: trimmedName,
            channel: form.channel || "email",
            subject: form.subject?.trim() || null,
            body: trimmedBody,
          },
          token
        );
      } else {
        await createFeedbackTemplate(
          salonId,
          {
            name: trimmedName,
            channel: form.channel || "email",
            subject: form.subject?.trim() || null,
            body: trimmedBody,
          },
          token
        );
      }
      setModalOpen(false);
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    if (!template?.id) return;
    setSaving(true);
    setError("");
    try {
      await updateFeedbackTemplate(
        template.id,
        { is_active: !template.is_active },
        token
      );
      await loadTemplates();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update template.");
    } finally {
      setSaving(false);
    }
  };

  const previewValues = useMemo(
    () => ({
      client_full_name: "Alex Client",
      client_phone: "(555) 010-1234",
      ticket_number: "T-20260204-001",
      service_date_local: new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      staff_name: "Jamie Tech",
      manager_name: displayName(user) || "Person sending email",
      salon_name:
        currentSalon?.name || currentSalon?.salon_name || user?.salon_name || "Salon",
      salon_phone:
        currentSalon?.phone ||
        currentSalon?.salon_phone ||
        currentSalon?.business_phone ||
        "(555) 555-1212",
    }),
    [currentSalon, user]
  );

  const previewSubject = renderTokens(form.subject, previewValues);
  const previewBody = renderTokens(form.body, previewValues);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 pr-20 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Response Templates
            </h1>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-full bg-white/15 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/25"
            >
              New template
            </button>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left w-64">Name</th>
                    <th className="px-3 py-2 text-left w-40">Channel</th>
                    <th className="px-3 py-2 text-left">Subject</th>
                    <th className="px-3 py-2 text-left w-32">Status</th>
                    <th className="px-3 py-2 text-right w-40">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading && (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={5}>
                        Loading templates...
                      </td>
                    </tr>
                  )}
                  {!loading && templates.length === 0 && (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={5}>
                        No templates created yet.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    templates.map((template) => (
                      <tr key={template.id} className="hover:bg-slate-50">
                        <td className="px-3 py-3 text-slate-700">
                          <div className="font-semibold text-slate-900">
                            {template.name}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-700 capitalize">
                          {template.channel}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {template.subject || "No subject"}
                        </td>
                        <td className="px-3 py-3 text-slate-700">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                              template.is_active
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-200 text-slate-600"
                            }`}
                          >
                            {template.is_active ? "Enabled" : "Disabled"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEdit(template)}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleActive(template)}
                              className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                                template.is_active
                                  ? "border-rose-200 text-rose-700 hover:bg-rose-50"
                                  : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                              }`}
                            >
                              {template.is_active ? "Disable" : "Enable"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <SBINModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? "Edit Template" : "New Template"}
        size="lg"
        fitContent
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save template"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Template name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Channel
              </label>
              <select
                value={form.channel}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, channel: event.target.value }))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="email">Email</option>
                <option value="sms" disabled>
                  SMS (coming soon)
                </option>
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Subject
            </label>
            <input
              type="text"
              value={form.subject}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, subject: event.target.value }))
              }
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Body
            </label>
            <textarea
              value={form.body}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, body: event.target.value }))
              }
              rows={6}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {TEMPLATE_TOKENS.map((item) => (
              <button
                key={item.token}
                type="button"
                title={`${item.label}: ${item.source}`}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    body: `${prev.body || ""}${prev.body ? "\n" : ""}${item.token}`,
                  }))
                }
                className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                {item.token}
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Where values come from
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
              {TEMPLATE_TOKENS.map((item) => (
                <div
                  key={`${item.token}-source`}
                  className="rounded-xl border border-slate-200 px-3 py-2"
                >
                  <div className="text-xs font-semibold text-slate-900">
                    {item.token}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {item.source}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-slate-500">
              If a value is missing, that spot is left blank in the final email.
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Preview
            </div>
            <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">
              <div className="font-semibold">{previewSubject}</div>
              <div className="mt-2">{previewBody}</div>
            </div>
          </div>
        </div>
      </SBINModal>
    </div>
  );
}
